// Alerting (P5): alerts with acknowledge, rules, and email channels
// (docs/modules/alerting.md).
import { call, callNoContent } from './fleetApi';

export type Severity = 'critical' | 'warning' | 'info';
export type RuleKind = 'appliance_offline' | 'endpoint_offline' | 'telemetry_absent';

export interface AlertItem {
  id: string;
  site_id: string;
  site_name: string;
  rule_id: string;
  kind: RuleKind;
  severity: Severity;
  subject_type: 'appliance' | 'endpoint';
  subject_id: string;
  title: string;
  condition_since: string;
  raised_at: string;
  resolved_at: string | null;
  acknowledged_at: string | null;
  /** The email of whoever acknowledged it. */
  acknowledged_by: string | null;
  ack_note: string | null;
  details: Record<string, unknown>;
}

export interface AlertEvent {
  ts: string;
  event: 'raised' | 'acknowledged' | 'unacknowledged' | 'resolved' | 'notified' | 'notify_failed';
  actor: string | null;
  details: Record<string, unknown>;
}

export interface Rule {
  id: string;
  site_id: string | null;
  name: string;
  kind: RuleKind;
  severity: Severity;
  for_seconds: number;
  enabled: boolean;
  channel_ids: string[];
}

export interface Channel {
  id: string;
  name: string;
  kind: 'email';
  config: { to: string[] };
  enabled: boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const hasArray =
  <K extends string>(key: K) =>
  <T>(v: unknown): v is Record<K, T[]> =>
    isObject(v) && Array.isArray(v[key]);
const hasId = (v: unknown): boolean => isObject(v) && typeof v.id === 'string';
const isAlertItem = (v: unknown): v is AlertItem => hasId(v);
const isRule = (v: unknown): v is Rule => hasId(v);
const isChannel = (v: unknown): v is Channel => hasId(v);
const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const tenant = (apiUrl: string, tenantId: string) => `${apiUrl}/v1/tenants/${tenantId}`;

export async function fetchAlerts(
  apiUrl: string,
  token: string,
  tenantId: string,
  state: 'open' | 'resolved',
  signal?: AbortSignal,
): Promise<AlertItem[]> {
  const body = await call(
    `${tenant(apiUrl, tenantId)}/alerts?state=${state}&limit=200`,
    token,
    { signal },
    hasArray('alerts')<AlertItem>,
  );
  return body.alerts;
}

export async function fetchAlert(
  apiUrl: string,
  token: string,
  tenantId: string,
  id: string,
  signal?: AbortSignal,
): Promise<{ alert: AlertItem; events: AlertEvent[] }> {
  return call(
    `${tenant(apiUrl, tenantId)}/alerts/${id}`,
    token,
    { signal },
    (v): v is { alert: AlertItem; events: AlertEvent[] } =>
      isObject(v) && isObject(v.alert) && Array.isArray(v.events),
  );
}

export async function acknowledge(
  apiUrl: string,
  token: string,
  tenantId: string,
  id: string,
  ack: boolean,
  note = '',
): Promise<AlertItem> {
  const url = `${tenant(apiUrl, tenantId)}/alerts/${id}/ack`;
  return call(url, token, ack ? json('POST', { note }) : { method: 'DELETE' }, isAlertItem);
}

export async function fetchRules(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<Rule[]> {
  const body = await call(
    `${tenant(apiUrl, tenantId)}/alert-rules`,
    token,
    { signal },
    hasArray('rules')<Rule>,
  );
  return body.rules;
}

export type RuleInput = Omit<Rule, 'id'>;

/**
 * The fields of a rule the API accepts on write. Rules read from the API
 * carry more (tenant_id, timestamps), and the API refuses unknown fields.
 */
export function toRuleInput(r: RuleInput): RuleInput {
  return {
    name: r.name,
    kind: r.kind,
    severity: r.severity,
    for_seconds: r.for_seconds,
    enabled: r.enabled,
    site_id: r.site_id,
    channel_ids: r.channel_ids,
  };
}

export async function saveRule(
  apiUrl: string,
  token: string,
  tenantId: string,
  rule: RuleInput,
  id?: string,
): Promise<Rule> {
  const base = `${tenant(apiUrl, tenantId)}/alert-rules`;
  const body = toRuleInput(rule);
  return id
    ? call(`${base}/${id}`, token, json('PUT', body), isRule)
    : call(base, token, json('POST', body), isRule);
}

export async function deleteRule(apiUrl: string, token: string, tenantId: string, id: string) {
  return callNoContent(`${tenant(apiUrl, tenantId)}/alert-rules/${id}`, token, {
    method: 'DELETE',
  });
}

export async function fetchChannels(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<Channel[]> {
  const body = await call(
    `${tenant(apiUrl, tenantId)}/alert-channels`,
    token,
    { signal },
    hasArray('channels')<Channel>,
  );
  return body.channels;
}

export type ChannelInput = Omit<Channel, 'id'>;

export async function saveChannel(
  apiUrl: string,
  token: string,
  tenantId: string,
  channel: ChannelInput,
  id?: string,
): Promise<Channel> {
  const base = `${tenant(apiUrl, tenantId)}/alert-channels`;
  // Only the fields the API accepts (it refuses unknown ones).
  const body: ChannelInput = {
    name: channel.name,
    kind: channel.kind,
    config: { to: channel.config.to },
    enabled: channel.enabled,
  };
  return id
    ? call(`${base}/${id}`, token, json('PUT', body), isChannel)
    : call(base, token, json('POST', body), isChannel);
}

export async function deleteChannel(apiUrl: string, token: string, tenantId: string, id: string) {
  return callNoContent(`${tenant(apiUrl, tenantId)}/alert-channels/${id}`, token, {
    method: 'DELETE',
  });
}

export async function testChannel(apiUrl: string, token: string, tenantId: string, id: string) {
  await call(
    `${tenant(apiUrl, tenantId)}/alert-channels/${id}/test`,
    token,
    { method: 'POST' },
    isObject,
  );
}

export const KIND_LABELS: Record<RuleKind, string> = {
  appliance_offline: 'Appliance offline',
  endpoint_offline: 'Lighting endpoint not responding',
  telemetry_absent: 'No telemetry',
};

/** 300 -> "5 min", 3600 -> "1 h", 5400 -> "1 h 30 min". */
export function formatHold(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${String(m)} min`;
  return m === 0 ? `${String(h)} h` : `${String(h)} h ${String(m)} min`;
}

/** "3 min", "2 h 5 min", "4 days": how long something has lasted. */
export function formatDuration(ms: number): string {
  const min = Math.max(0, Math.floor(ms / 60000));
  if (min < 1) return 'under a minute';
  if (min < 60) return `${String(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return min % 60 === 0 ? `${String(h)} h` : `${String(h)} h ${String(min % 60)} min`;
  return `${String(Math.floor(h / 24))} days`;
}

/** Splits "a@x.com, b@y.com\nc@z.com" into addresses. */
export function parseRecipients(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}
